import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getSocket } from '../services/socket';
import { fetchNotifications, fetchUnreadCount, markRead, markUnread, deleteNotifications } from '../services/notifications';
import { useAuth } from './authContext';
import { useSite } from './siteContext';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export default function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { selectedSiteKey, loading: siteLoading } = useSite();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastNewEventToken, setLastNewEventToken] = useState(0);

  const refreshNotifications = useCallback(async (limit = 500, offset = 0, q = "") => {
    if (!isAuthenticated) return;
    const [count, listResponse] = await Promise.all([
      fetchUnreadCount({ selectedSiteKey }),
      fetchNotifications({ limit, offset, q, selectedSiteKey }),
    ]);
    setUnread(count || 0);
    setItems(Array.isArray(listResponse?.items) ? listResponse.items : []);
  }, [isAuthenticated, selectedSiteKey]);

  const refreshNotificationsRef = useRef(refreshNotifications);
  refreshNotificationsRef.current = refreshNotifications;

  useEffect(() => {
    if (!isAuthenticated) {
      setUnread(0);
      setItems([]);
      return;
    }
    if (siteLoading) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [count, listResponse] = await Promise.all([
          fetchUnreadCount({ selectedSiteKey }),
          fetchNotifications({ limit: 500, offset: 0, selectedSiteKey }),
        ]);
        if (!mounted) return;
        setUnread(count ?? 0);
        setItems(Array.isArray(listResponse?.items) ? listResponse.items : []);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [isAuthenticated, siteLoading, selectedSiteKey]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const s = getSocket();
    const handler = () => {
      setLastNewEventToken(Date.now());
      refreshNotificationsRef.current(500, 0);
    };
    s.on('notifications:new', handler);
    return () => { s.off('notifications:new', handler); };
  }, [isAuthenticated]);

  const markAllRead = async () => {
    const ids = items.filter(i => !i.read_at && i.recipient_id).map(i => i.recipient_id);
    if (ids.length) {
      await markRead(ids);
    }
    setUnread(0);
    setItems(prev => prev.map(i => ({ ...i, read_at: i.read_at || new Date().toISOString() })));
  };

  const markOneReadLocal = (recipientId) => {
    if (!recipientId) return;
    setItems(prev => prev.map(i => i.recipient_id === recipientId ? { ...i, read_at: i.read_at || new Date().toISOString() } : i));
    setUnread(u => Math.max(0, u - 1));
  };

  const markOneUnreadLocal = (recipientId) => {
    if (!recipientId) return;
    setItems(prev => prev.map(i => i.recipient_id === recipientId ? { ...i, read_at: null } : i));
    setUnread(u => u + 1);
  };

  const markOneRead = useCallback(async (recipientId) => {
    if (!recipientId) return;
    await markRead([recipientId]);
    let changed = false;
    setItems(prev => prev.map(i => {
      if (i.recipient_id === recipientId) {
        if (!i.read_at) changed = true;
        return { ...i, read_at: i.read_at || new Date().toISOString() };
      }
      return i;
    }));
    if (changed) {
      setUnread(u => Math.max(0, u - 1));
    }
  }, []);

  const markOneUnread = useCallback(async (recipientId) => {
    if (!recipientId) return;
    await markUnread([recipientId]);
    let changed = false;
    setItems(prev => prev.map(i => {
      if (i.recipient_id === recipientId) {
        if (i.read_at) changed = true;
        return { ...i, read_at: null };
      }
      return i;
    }));
    if (changed) {
      setUnread(u => u + 1);
    }
  }, []);

  const removeRecipientsLocal = (recipientIds = []) => {
    if (!recipientIds.length) return;
    setItems(prev => {
      const unreadRemoved = prev.filter(i => recipientIds.includes(i.recipient_id) && !i.read_at).length;
      setUnread(prevUnread => Math.max(0, prevUnread - unreadRemoved));
      return prev.filter(i => !recipientIds.includes(i.recipient_id));
    });
  };

  const deleteRecipients = useCallback(async (recipientIds = []) => {
    if (!recipientIds.length) return;
    await deleteNotifications(recipientIds);
    setItems(prev => {
      const unreadRemoved = prev.filter(i => recipientIds.includes(i.recipient_id) && !i.read_at).length;
      setUnread(prevUnread => Math.max(0, prevUnread - unreadRemoved));
      return prev.filter(i => !recipientIds.includes(i.recipient_id));
    });
  }, []);

  const value = useMemo(() => ({
    items,
    unread,
    loading,
    markAllRead,
    markOneRead,
    markOneUnread,
    markOneReadLocal,
    markOneUnreadLocal,
    removeRecipientsLocal,
    deleteRecipients,
    lastNewEventToken,
    refreshNotifications,
  }), [items, unread, loading, lastNewEventToken, refreshNotifications, markOneRead, markOneUnread, deleteRecipients]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
